import { useEffect, useRef, useState } from "react";
import RoundTable from "./RoundTable";

const TABLE_SIZE = 220;
const ROOM_WIDTH = 1400;
const ROOM_HEIGHT = 900;

// Visual presets per furniture type (icon + default size + colors)
const FURNITURE_STYLES = {
  dance: {
    label: "Tanzfläche",
    bg: "rgba(132,166,138,0.18)",
    border: "rgba(85,126,92,0.7)",
    text: "#3F6248",
    font: "font-script text-3xl",
    defaultW: 400,
    defaultH: 200,
  },
  bar: {
    label: "Bar",
    bg: "rgba(85,126,92,0.18)",
    border: "rgba(85,126,92,0.7)",
    text: "#3F6248",
    font: "font-serif text-xl tracking-widest uppercase",
    defaultW: 360,
    defaultH: 90,
  },
  buffet: {
    label: "Buffet",
    bg: "rgba(196,180,140,0.25)",
    border: "rgba(160,140,100,0.7)",
    text: "#6B5A2E",
    font: "font-serif text-xl tracking-widest uppercase",
    defaultW: 360,
    defaultH: 90,
  },
  stage: {
    label: "Bühne",
    bg: "rgba(140,120,100,0.18)",
    border: "rgba(110,90,80,0.7)",
    text: "#5A4632",
    font: "font-serif text-xl tracking-widest uppercase",
    defaultW: 340,
    defaultH: 160,
  },
  door: {
    label: "Eingang",
    bg: "rgba(255,255,255,0.6)",
    border: "rgba(85,126,92,0.6)",
    text: "#3F6248",
    font: "font-serif text-base tracking-widest uppercase",
    defaultW: 140,
    defaultH: 60,
  },
  custom: {
    label: "Bereich",
    bg: "rgba(85,126,92,0.12)",
    border: "rgba(85,126,92,0.5)",
    text: "#3F6248",
    font: "font-serif text-lg",
    defaultW: 220,
    defaultH: 120,
  },
};

export const getFurnitureStyle = (type) => FURNITURE_STYLES[type] || FURNITURE_STYLES.custom;

/**
 * RoomCanvas — drag-and-drop floor plan with tables AND movable furniture.
 * Props:
 *   tables, furniture
 *   onMoveTable(id, x, y)
 *   onMoveFurniture(id, x, y)
 *   onSeatClick(tableId, seatIdx)
 *   onTableDropName(tableId, name)  — fired when a guest name is dropped onto a table
 *   onFurnitureClick(id)            — admin: open editor (rename/resize/delete)
 *   readOnly: bool                  — public mode
 */
export const RoomCanvas = ({
  tables,
  furniture = [],
  onMoveTable,
  onMoveFurniture,
  onSeatClick,
  onTableDropName,
  onFurnitureClick,
  readOnly = false,
  forcedScale = null,
}) => {
  const wrapRef = useRef(null);
  const dragRef = useRef(null);
  const [scale, setScale] = useState(forcedScale ?? 0.7);
  const [hoverTableId, setHoverTableId] = useState(null);

  useEffect(() => {
    if (forcedScale !== null) {
      setScale(forcedScale);
      return undefined;
    }
    const compute = () => {
      const w = wrapRef.current?.clientWidth || 800;
      const next = Math.min(1, Math.max(0.3, (w - 32) / ROOM_WIDTH));
      setScale(next);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [forcedScale]);

  const startDrag = (e, kind, item) => {
    if (readOnly) return;
    if (e.target.closest("button[data-testid^='seat-']")) return;
    e.preventDefault();
    const target = e.currentTarget;
    target.setPointerCapture?.(e.pointerId);
    dragRef.current = {
      kind, // 'table' | 'furniture'
      id: item.id,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origX: item.x || 0,
      origY: item.y || 0,
      itemW: kind === "table" ? TABLE_SIZE : item.w || 200,
      itemH: kind === "table" ? TABLE_SIZE : item.h || 120,
      moved: false,
    };
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const dx = (e.clientX - d.startX) / scale;
    const dy = (e.clientY - d.startY) / scale;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) d.moved = true;
    const nx = Math.max(0, Math.min(ROOM_WIDTH - d.itemW, d.origX + dx));
    const ny = Math.max(0, Math.min(ROOM_HEIGHT - d.itemH, d.origY + dy));
    if (d.kind === "table") onMoveTable?.(d.id, nx, ny);
    else onMoveFurniture?.(d.id, nx, ny);
  };

  const onPointerUp = (e) => {
    if (dragRef.current?.pointerId === e.pointerId) {
      dragRef.current = null;
    }
  };

  // HTML5 drag-and-drop targets for guest name drops onto tables
  const handleDragOver = (e, tableId) => {
    if (readOnly) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (hoverTableId !== tableId) setHoverTableId(tableId);
  };

  const handleDragLeave = () => setHoverTableId(null);

  const handleDrop = (e, tableId) => {
    if (readOnly) return;
    e.preventDefault();
    setHoverTableId(null);
    const name = e.dataTransfer.getData("text/plain");
    if (name && name.trim()) {
      onTableDropName?.(tableId, name.trim());
    }
  };

  return (
    <div
      ref={wrapRef}
      data-testid="room-canvas"
      className="w-full overflow-auto bg-sage-50 border border-sage-200 rounded-2xl"
      style={{ touchAction: "pan-x pan-y" }}
    >
      <div
        className="relative origin-top-left"
        style={{
          width: ROOM_WIDTH,
          height: ROOM_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: "0 0",
          marginRight: ROOM_WIDTH * (scale - 1),
          marginBottom: ROOM_HEIGHT * (scale - 1),
        }}
      >
        {/* Floor grid */}
        <div
          aria-hidden="true"
          className="absolute inset-0 rounded-xl"
          style={{
            backgroundColor: "#FAFAF7",
            backgroundImage:
              "linear-gradient(to right, rgba(85,126,92,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(85,126,92,0.08) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            border: "1px dashed rgba(85,126,92,0.3)",
          }}
        />

        {/* Furniture (rectangles) */}
        {furniture.map((f) => {
          const s = getFurnitureStyle(f.type);
          const label = f.name || s.label;
          return (
            <div
              key={f.id}
              data-testid={`canvas-furniture-${f.id}`}
              onPointerDown={(e) => startDrag(e, "furniture", f)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              onClick={() => {
                if (!dragRef.current && !readOnly) onFurnitureClick?.(f.id);
              }}
              className={`absolute select-none rounded-xl flex items-center justify-center text-center ${
                readOnly
                  ? "cursor-default"
                  : "cursor-grab active:cursor-grabbing hover:ring-2 hover:ring-sage-400"
              }`}
              style={{
                left: f.x || 0,
                top: f.y || 0,
                width: f.w || s.defaultW,
                height: f.h || s.defaultH,
                backgroundColor: s.bg,
                border: `2px solid ${s.border}`,
                color: s.text,
              }}
            >
              <span className={`px-3 ${s.font}`} style={{ color: s.text }}>
                {label}
              </span>
            </div>
          );
        })}

        {/* Tables */}
        {tables.map((t) => {
          const isHover = hoverTableId === t.id;
          return (
            <div
              key={t.id}
              data-testid={`canvas-table-${t.id}`}
              onPointerDown={(e) => startDrag(e, "table", t)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              onDragOver={(e) => handleDragOver(e, t.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, t.id)}
              className={`absolute select-none transition-shadow ${
                readOnly ? "cursor-default" : "cursor-grab active:cursor-grabbing"
              } ${isHover ? "ring-4 ring-sage-500 ring-offset-2 ring-offset-sage-50 rounded-full" : ""}`}
              style={{
                left: t.x || 0,
                top: t.y || 0,
                width: TABLE_SIZE,
                height: TABLE_SIZE + 36,
              }}
            >
              <RoundTable
                table={t}
                size={TABLE_SIZE}
                onSeatClick={readOnly ? undefined : (idx) => onSeatClick?.(t.id, idx)}
                highlightEmpty={!readOnly}
              />
              <div className="text-center mt-1 text-xs text-sage-700 font-medium pointer-events-none">
                {(t.assignments || []).filter(Boolean).length} / {t.seats}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RoomCanvas;
